import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts'
import { getProgressColor } from '@/lib/constants'

interface ParcelRow {
  parcel_id:     string
  parcel_name:   string
  parcel_number: number
  progress_pct:  number
}

interface Props {
  data:    ParcelRow[]
  loading: boolean
}

function CustomTooltip({ active, payload, label }: {
  active?: boolean
  payload?: Array<{ name: string; value: number }>
  label?: string
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-800 mb-1">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex items-center justify-between gap-4">
          <span className="text-gray-500">{p.name}</span>
          <span className="font-bold text-gray-900">{Number(p.value).toFixed(1)}%</span>
        </div>
      ))}
    </div>
  )
}

export default function ProgressByParcelChart({ data, loading }: Props) {
  if (loading) {
    return (
      <div className="card p-5">
        <div className="h-5 w-40 bg-gray-200 rounded mb-4 animate-pulse" />
        <div className="h-72 bg-gray-100 rounded animate-pulse" />
      </div>
    )
  }

  const sorted = [...data].sort((a, b) => a.parcel_number - b.parcel_number)

  const chartData = sorted.map(p => ({
    name:     `P${p.parcel_number}`,
    fullName: p.parcel_name,
    reported: Math.round(p.progress_pct * 10) / 10,
    color:    getProgressColor(p.progress_pct),
  }))

  const avg = data.length ? data.reduce((s, p) => s + p.progress_pct, 0) / data.length : 0

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Avance por Parcela</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Media: <span className="font-semibold text-gray-700">{avg.toFixed(1)}%</span>
          </p>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 50, left: 10, bottom: 0 }} barCategoryGap="20%">
          <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="#F3F4F6" />
          <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`}
            tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#6B7280' }}
            axisLine={false} tickLine={false} width={24} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F9FAFB' }} />
          <ReferenceLine x={avg} stroke="#FB923C" strokeDasharray="4 4" strokeWidth={1.5}
            label={{ position: 'top', value: `Ø ${avg.toFixed(0)}%`, fontSize: 9, fill: '#FB923C' }} />
          <Bar dataKey="reported" name="Avance" radius={[0, 3, 3, 0]} maxBarSize={10}>
            {chartData.map((entry, idx) => (
              <Cell key={idx} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
